// @flow

import React, { Fragment } from 'react';
import { Spacer } from '@zapier/design-system';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import { getUserFields } from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

export const createPartnerPortal = (isLoggedIn: boolean) => ({
  value: 'partner-portal',
  label: 'I have questions about the Partner Portal',
  child: help({
    skipInterstitial: true,
    docs: {
      title: 'Solution Partner Portal',
      body: () => (
        <Fragment>
          <p>Here are answers to common Partner Portal questions:</p>
          <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
            <li>
              <span style={{ fontWeight: 'bold' }}>
                How do I log into the Partner Portal?
              </span>{' '}
              You can login at{' '}
              <a
                href="https://partnerportal.zapier.com/"
                rel="noopener noreferrer"
                target="_blank"
              >
                https://partnerportal.zapier.com/
              </a>
            </li>
            <li>
              <span style={{ fontWeight: 'bold' }}>
                How can I reset my password?
              </span>{' '}
              You can reset your password at{' '}
              <a
                href="https://partnerportal.zapier.com/user/forgotpassword/"
                rel="noopener noreferrer"
                target="_blank"
              >
                https://partnerportal.zapier.com/user/forgotpassword/
              </a>
            </li>
            <li>
              <span style={{ fontWeight: 'bold' }}>
                I can't find my invite email to create an account on the partner
                portal.
              </span>{' '}
              The email should have the subject "Zapier Partner Portal
              Instructions", search for it in your inbox
            </li>
            <li>
              <span style={{ fontWeight: 'bold' }}>
                I can't find my profile in the Solution Partner Directory.
              </span>{' '}
              Your profile needs to be reviewed and approved by Zapier before it
              becomes publicly available
            </li>
          </ul>
        </Fragment>
      ),
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'message',
        type: 'text',
        label: 'Describe your issue or question',
        isMultiline: true,
        isRequired: true,
      },
      {
        id: 'portal_email',
        type: 'text',
        label:
          'Provide the email address you use to log into the Partner Portal',
        isRequired: true,
      },
      {
        id: 'company_name',
        type: 'text',
        label: 'Provide your company name as it appears in the Partner Portal',
        isRequired: true,
      },
      {
        id: 'custom_field_33382613130125',
        type: 'hidden',
        value: 'expsup-expert-general',
        zendeskCustomFieldId: 33382613130125,
      },
      {
        id: 'custom_field_33382715306125',
        type: 'hidden',
        value: 'expsup-expert-general-partner-listing',
        zendeskCustomFieldId: 33382715306125,
      },
    ],
  }),
});

export const createAccountStatus = (isLoggedIn: boolean) => ({
  value: 'account-status',
  label: 'I have questions about my account status or tier',
  child: help({
    skipInterstitial: true,
    docs: {
      title: 'Account status and tier',
      body: () => (
        <Fragment>
          <p>
            When you're first approved to join the Zapier Solution Partner
            Program, you’ll start at the Provisional Solution Partner stage. At
            this stage, you're in our program, but haven't yet become an
            authorized Solution Partner. While in this stage, you can:
          </p>

          <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
            <li>Submit referral clients and begin earning commission</li>
            <li>
              Access onboarding and training materials to prepare for full
              partner status
            </li>
          </ul>

          <Spacer height={16} />

          <p>
            Once you complete all onboarding requirements, you'll graduate from
            Provisional to Silver Solution Partner.
          </p>

          <Spacer height={16} />

          <p>
            Partners are tiered based on a combination of the new customers they
            help bring to Zapier and the ongoing client relationships they
            support through implementation or management services.
          </p>

          <Spacer height={16} />

          <p>
            <a
              href="https://share.hsforms.com/1BVFH0zJTQ86GHcButJfjKw4fxs5"
              rel="noopener noreferrer"
              style={{ fontWeight: 600 }}
              target="_blank"
            >
              Not a Solution Partner yet? Apply here!
            </a>
          </p>
        </Fragment>
      ),
      links: [
        {
          href: 'https://partnerportal.zapier.com/wikis/help/TierUp',
          title: 'Tiering Up in the Solution Partner Program',
        },
        {
          href: 'https://partnerportal.zapier.com/wikis/help/levels',
          title: 'Levels of Solution Partner',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'message',
        type: 'text',
        label: 'Describe your issue or question',
        isMultiline: true,
        isRequired: true,
      },
      {
        id: 'portal_email',
        type: 'text',
        label:
          'Provide the email address you use to log into the Partner Portal',
        isRequired: true,
      },
      {
        id: 'company_name',
        type: 'text',
        label: 'Provide your company name as it appears in the Partner Portal',
        isRequired: true,
      },
      {
        id: 'custom_field_33382613130125',
        type: 'hidden',
        value: 'expsup-expert-general',
        zendeskCustomFieldId: 33382613130125,
      },
      {
        id: 'custom_field_33382715306125',
        type: 'hidden',
        value: 'expsup-expert-general-status-tiering',
        zendeskCustomFieldId: 33382715306125,
      },
    ],
  }),
});

export const createBillingPayment = (isLoggedIn: boolean) => ({
  value: 'billing-payment',
  label: 'I have questions about billing or payments',
  child: help({
    skipInterstitial: true,
    docs: {
      title: 'Billing and Payments',
      body: () => (
        <Fragment>
          <p>
            The Zapier Referral Program rewards Solution Partners who introduce
            net-new customers to Zapier. You can earn commissions when your
            referred clients subscribe to a paid Zapier plan.
          </p>

          <Spacer height={16} />

          <p>
            Once submitted, Zapier's system checks the lead against our CRM to
            determine eligibility. Referral commission is calculated and paid
            out monthly. You can monitor referral status and commissions via the
            Partner Portal dashboard's "Rewards" tab.
          </p>

          <Spacer height={16} />

          <p>
            <a
              href="https://share.hsforms.com/1BVFH0zJTQ86GHcButJfjKw4fxs5"
              rel="noopener noreferrer"
              style={{ fontWeight: 600 }}
              target="_blank"
            >
              Not a Solution Partner yet? Apply here!
            </a>
          </p>
        </Fragment>
      ),
      links: [
        {
          href:
            'https://partnerportal.zapier.com/wikis/help/ReferralProgramOverview',
          title: 'Zapier Referral Program Overview',
        },
        {
          href:
            'https://partnerportal.zapier.com/wikis/help/RegisteringNewDeals',
          title: 'Registering New Deals',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'message',
        type: 'text',
        label: 'Describe your issue or question',
        isMultiline: true,
        isRequired: true,
      },
      {
        id: 'portal_email',
        type: 'text',
        label:
          'Provide the email address you use to log into the Partner Portal',
        isRequired: true,
      },
      {
        id: 'company_name',
        type: 'text',
        label: 'Provide your company name as it appears in the Partner Portal',
        isRequired: true,
      },
      {
        id: 'lead_email',
        type: 'text',
        label:
          'Provide the email address of the lead you want to enquire about',
        isRequired: false,
      },
      {
        id: 'custom_field_33382613130125',
        type: 'hidden',
        value: 'expsup-expert-billing-payments',
        zendeskCustomFieldId: 33382613130125,
      },
      {
        id: 'custom_field_38223296515213',
        type: 'hidden',
        value: 'expsup-expert-billing-payouts',
        zendeskCustomFieldId: 38223296515213,
      },
    ],
  }),
});

export const createOnboardingJourneys = (isLoggedIn: boolean) => ({
  value: 'onboarding-journeys',
  label: 'I have questions about onboarding journeys',
  child: help({
    skipInterstitial: true,
    docs: {
      title: 'Onboarding Journeys',
      body: () => (
        <Fragment>
          <p>
            To become a Authorized Solution Partner with Zapier, you'll need to
            complete the Onboarding Journeys and General Access Learning
            Pathway. Completion of these journeys is essential not only for
            tiering up but also for gaining a solid understanding of how to
            succeed as a Zapier Solution Partner. Each journey covers critical
            aspects of the program, from working with clients to understanding
            your partner benefits. These might include reading materials,
            watching videos, or submitting information.
          </p>

          <Spacer height={16} />

          <p>
            <a
              href="https://share.hsforms.com/1BVFH0zJTQ86GHcButJfjKw4fxs5"
              rel="noopener noreferrer"
              style={{ fontWeight: 600 }}
              target="_blank"
            >
              Not a Solution Partner yet? Apply here!
            </a>
          </p>
        </Fragment>
      ),
      links: [
        {
          href: 'https://partnerportal.zapier.com/wikis/help/Onboarding',
          title: 'Onboarding Journeys',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'message',
        type: 'text',
        label: 'Describe your issue or question',
        isMultiline: true,
        isRequired: true,
      },
      {
        id: 'portal_email',
        type: 'text',
        label:
          'Provide the email address you use to log into the Partner Portal',
        isRequired: true,
      },
      {
        id: 'company_name',
        type: 'text',
        label: 'Provide your company name as it appears in the Partner Portal',
        isRequired: true,
      },
      {
        id: 'custom_field_33382613130125',
        type: 'hidden',
        value: 'expsup-expert-portal',
        zendeskCustomFieldId: 33382613130125,
      },
      {
        id: 'custom_field_33382979600397',
        type: 'hidden',
        value: 'expsup-expert-portal-journeys',
        zendeskCustomFieldId: 33382979600397,
      },
    ],
  }),
});
